import { Box, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core'
import {
  IconDatabase,
  IconNetwork,
  IconServer,
  IconTargetArrow,
  IconWorld,
} from '@tabler/icons-react'
import type { TopologyNode } from '@/api/topology'

interface NetworkFlowProps {
  nodes: TopologyNode[]
  cwMetrics: any
  loading?: boolean
}

type NodeType = 'domain' | 'alb' | 'target_group' | 'ec2' | 'rds'
type NodeStatus = 'healthy' | 'warning' | 'critical'

const STATUS_COLOR: Record<NodeStatus, string> = {
  healthy: 'var(--mantine-color-teal-6)',
  warning: 'var(--mantine-color-yellow-6)',
  critical: 'var(--mantine-color-red-6)',
}

const STATUS_PRIORITY: Record<NodeStatus, number> = {
  healthy: 0,
  warning: 1,
  critical: 2,
}

const COLUMNS: {
  type: NodeType
  label: string
  Icon: React.ElementType
}[] = [
  { type: 'domain', label: 'DNS', Icon: IconWorld },
  { type: 'alb', label: 'ALB', Icon: IconNetwork },
  { type: 'target_group', label: 'Target Groups', Icon: IconTargetArrow },
  { type: 'ec2', label: 'EC2', Icon: IconServer },
  { type: 'rds', label: 'RDS', Icon: IconDatabase },
]

function worstStatus(statuses: NodeStatus[]): NodeStatus {
  if (statuses.length === 0) return 'healthy'
  return statuses.reduce<NodeStatus>((worst, s) =>
    STATUS_PRIORITY[s] > STATUS_PRIORITY[worst] ? s : worst,
    'healthy'
  )
}

function getKeyMetric(type: NodeType, cwMetrics: any): string | null {
  if (!cwMetrics) return null
  try {
    if (type === 'alb') {
      const latency = cwMetrics?.alb?.latency_p99 ?? cwMetrics?.alb?.latency
      if (latency != null) return `${(latency * 1000).toFixed(0)} ms p99`
    }
    if (type === 'ec2') {
      const cpu = cwMetrics?.ec2?.cpu_avg ?? cwMetrics?.ec2?.cpu
      if (cpu != null) return `CPU ${cpu.toFixed(1)}%`
    }
    if (type === 'rds') {
      const lat = cwMetrics?.rds?.read_latency ?? cwMetrics?.rds?.latency
      if (lat != null) return `${(lat * 1000).toFixed(0)} ms leit.`
    }
  } catch {
    // ignore parse errors
  }
  return null
}

export function NetworkFlow({ nodes, cwMetrics, loading }: NetworkFlowProps) {
  const grouped = COLUMNS.map(col => {
    const colNodes = nodes.filter(n => n.data.type === col.type)
    const statuses = colNodes.map(n => n.data.status as NodeStatus)
    const status = worstStatus(statuses)
    const metric = getKeyMetric(col.type, cwMetrics)
    return { ...col, count: colNodes.length, status, metric }
  })

  return (
    <Box
      style={{
        background: 'var(--ai-input-bg)',
        border: '1px solid var(--ai-border-light)',
        borderRadius: 'var(--mantine-radius-md)',
        padding: '16px 20px',
        overflowX: 'auto',
      }}
    >
      <style>{`
        @keyframes flowPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 0.8; } }
        .nf-arrow { animation: flowPulse 2s ease-in-out infinite; }
      `}</style>

      <Group gap={0} align="stretch" wrap="nowrap" justify="center">
        {grouped.map((col, i) => (
          <Group key={col.type} gap={0} align="center" wrap="nowrap">
            {/* Column card */}
            <Stack
              align="center"
              gap={6}
              style={{
                minWidth: 110,
                padding: '12px 8px',
                borderRadius: 'var(--mantine-radius-sm)',
                border: `1px solid ${STATUS_COLOR[col.status]}33`,
                background: 'var(--ai-card-bg)',
              }}
            >
              {loading ? (
                <>
                  <Skeleton circle height={36} width={36} />
                  <Skeleton height={10} width={60} radius="xl" />
                  <Skeleton height={8} width={40} radius="xl" />
                </>
              ) : (
                <>
                  <ThemeIcon
                    size={36}
                    radius="xl"
                    variant="light"
                    color={
                      col.status === 'healthy' ? 'teal' :
                      col.status === 'warning' ? 'yellow' : 'red'
                    }
                  >
                    <col.Icon size={18} />
                  </ThemeIcon>

                  <Text size="xs" fw={600} c="dimmed" ta="center" lh={1.2}>
                    {col.label}
                  </Text>

                  <Text size="sm" fw={700} style={{ color: STATUS_COLOR[col.status] }}>
                    {col.count > 0 ? col.count : '—'}
                    {col.count > 0 && (
                      <Text component="span" size="xs" c="dimmed" fw={400}> nós</Text>
                    )}
                  </Text>

                  {col.metric && (
                    <Text size="xs" c="dimmed" ta="center">
                      {col.metric}
                    </Text>
                  )}

                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: STATUS_COLOR[col.status],
                      boxShadow: `0 0 6px ${STATUS_COLOR[col.status]}`,
                    }}
                  />
                </>
              )}
            </Stack>

            {/* Arrow between columns */}
            {i < grouped.length - 1 && (
              <Box
                className="nf-arrow"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px',
                  color: 'var(--ai-text-muted)',
                  fontSize: 20,
                  userSelect: 'none',
                }}
              >
                {'→'}
              </Box>
            )}
          </Group>
        ))}
      </Group>
    </Box>
  )
}
